import {
  collection,
  addDoc,
  doc,
  serverTimestamp,
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
  DocumentData,
  WithFieldValue,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { firestore } from '@/lib/firebase';
import {
  useCollectionData,
  useDocumentData,
} from 'react-firebase-hooks/firestore';
import { useMemo } from 'react';

export enum TaskCommand {
  Reboot = 'REBOOT',
  ScreenCapture = 'SCREEN_CAPTURE',
  StartApp = 'START_APP',
  StopApp = 'STOP_APP',
  PowerOn = 'POWER_ON',
  PowerOff = 'POWER_OFF',
  TvOn = 'TV_ON',
  TvOff = 'TV_OFF',
  CheckAppVersion = 'CHECK_APP_VERSION',
  CheckAndroidVersion = 'CHECK_ANDROID_VERSION',
  Ping = 'PING',
  // Agent runs `adb shell input keyevent <data.keycode>` then a screen capture.
  KeyEvent = 'KEY_EVENT',
  // Agent dumps `adb logcat -d` and uploads it to Storage (execution.logUri).
  GetLogcat = 'GET_LOGCAT',
  // Agent downloads data.storagePath from Storage and runs `adb install -r`.
  // data: { storagePath, apkName, brand } — brand is re-checked agent-side.
  InstallApk = 'INSTALL_APK',
}

export type TaskResult = {
  ip: string;
  status: string;
};

export enum TaskStatus {
  Created = 'CREATED',
  Completed = 'COMPLETED',
  Failed = 'FAILED',
}

export type Task = {
  id?: string;
  task_initiated_date: Date;
  task_completed_date: Date;
  task_command: TaskCommand;
  boxes: string[];
  status: TaskStatus;
  results: TaskResult[];

  isTvControllerTask: boolean;
  data?: any;
};

export type TaskRequest = {
  command: TaskCommand;
  data?: any;
  boxes: string[];
  isTvControllerTask: boolean;
};

const taskConverter: FirestoreDataConverter<Task> = {
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options: SnapshotOptions
  ): Task {
    const data = snapshot.data(options) as any;
    if (data.task_initiated_date) {
      data.task_initiated_date = data.task_initiated_date.toDate();
    }
    if (data.task_completed_date) {
      data.task_completed_date = data.task_completed_date.toDate();
    }
    return {
      ...data,
      id: snapshot.id,
    };
  },
  toFirestore: function (): WithFieldValue<DocumentData> {
    throw new Error('Function not implemented.');
  },
};

export async function createTask(taskRequest: TaskRequest) {
  const task = {
    task_completed_date: null,
    task_initiated_date: serverTimestamp(),
    status: TaskStatus.Created,
    task_command: taskRequest.command,
    boxes: taskRequest.boxes,
    results: [],
    isTvControllerTask: taskRequest.isTvControllerTask,
    ...(taskRequest.data && { data: taskRequest.data }),
  };
  const doc = await addDoc(collection(firestore, 'task'), {
    ...task,
  });
  return doc.id;
}

/** How long waitforTask waits before releasing its listener. Without this, a
 *  task the agent never marks terminal (agent down, box unreachable) would
 *  leave a Firestore listener running for the rest of the session — and they
 *  accumulate with every command sent. */
const WAIT_FOR_TASK_TIMEOUT_MS = 5 * 60 * 1000;

export function waitforTask(id: string) {
  return new Promise<Task>((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(
      () =>
        settle(() =>
          reject(
            new Error(
              `Task ${id} did not complete within ${WAIT_FOR_TASK_TIMEOUT_MS / 60_000} minutes`
            )
          )
        ),
      WAIT_FOR_TASK_TIMEOUT_MS
    );
    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsubscribe();
      fn();
    };
    const unsubscribe = onSnapshot(
      doc(firestore, 'task', id).withConverter(taskConverter),
      (snapshot) => {
        const task = snapshot.data();
        if (task && task.status === TaskStatus.Completed) {
          settle(() => resolve(task));
        }
        if (task && task.status === TaskStatus.Failed) {
          settle(() =>
            reject(Object.assign(new Error(`Task ${id} failed`), { task }))
          );
        }
      },
      (error) => settle(() => reject(error))
    );
  });
}

export function useTaskById(id?: string) {
  // Guard: doc() throws on an undefined segment, which would crash the page.
  const ref = useMemo(
    () =>
      id ? doc(firestore, 'task', id).withConverter(taskConverter) : undefined,
    [id]
  );
  return useDocumentData(ref, {
    snapshotListenOptions: { includeMetadataChanges: false },
  });
}

/** Today's box tasks (newest first) for the previous-tasks picker. */
export function useTodaysBoxTasks() {
  const todaysQuery = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return query(
      collection(firestore, 'task').withConverter(taskConverter),
      where('isTvControllerTask', '==', false),
      where('task_initiated_date', '>=', Timestamp.fromDate(start)),
      orderBy('task_initiated_date', 'desc'),
      // Bound the result: every action (incl. each D-pad press) creates a task
      // doc, so an unbounded query would load thousands over a session and the
      // picker's <Select> would lock the main thread rendering them all.
      // KeyEvent docs are filtered out in the UI, so keep some headroom.
      limit(100)
    );
  }, []);

  return useCollectionData(todaysQuery, {
    snapshotListenOptions: { includeMetadataChanges: false },
  });
}
